import { faEllipsis, faExclamationTriangle, faGraduationCap, faCheck } from '@fortawesome/pro-solid-svg-icons';
import { faCalendar } from '@fortawesome/pro-regular-svg-icons/faCalendar';
import { faAlarmClock } from '@fortawesome/pro-regular-svg-icons/faAlarmClock';

import { useEffect, useMemo, useState } from 'react';
import { useCurrentUser, useDateHelper, useNavigationHelper, useTargetBrandConfig } from 'FrontRoyalAngular';
import { type LearnerProject } from 'LearnerProjects';
import { type StreamForDashboard } from 'Lessons';
import { CurriculumStatus, isPreGraduationStatus } from 'ProgramInclusion';
import { getProgramInclusion, getCurriculumStatus, getCohort } from 'Users';
import {
    ProgramTypeConfigs,
    refersToGraduation,
    showsCertificateCheckInWhenInFinishedState,
    showsDiplomaCheckInWhenInFinishedState,
    supportsCertificate,
} from 'Program';
import { InlineLink } from 'InlineLink';
import { useTranslation } from 'react-i18next';
import { formattedUserFacingMonthDayYearLong } from 'DateHelpers';
import { type I18NLocale } from 'Locale';
import { type StatusConfig, type StatusConfigPromise } from './StatusBox.types';
import { ACADEMIC_PROBATION_ADVISING_LINK } from './constants';
import { usePastDueCoursesAndProjects } from './usePastDueCoursesAndProjects';

type UseStatusBoxConfigReturn = {
    statusConfig: StatusConfig | null;
    allPastDueCourses: StreamForDashboard[];
    allPastDueProjects: LearnerProject[];
};

/**
 * useStatusBoxConfig - Custom hook that determines user's curriculum status
 *
 * This hook:
 * - Determines curriculum status based on user's progress
 * - Handles academic probation override (takes precedence over curriculum status)
 * - Provides status-specific configuration for display (icons, messages, headings, etc.)
 */
// oxlint-disable-next-line max-lines-per-function
export const useStatusBoxConfig = (): UseStatusBoxConfigReturn => {
    const { t } = useTranslation('back_royal', { keyPrefix: 'statusBox' });
    const [resolvedConfig, setResolvedConfig] = useState<StatusConfig | null>(null);
    const currentUser = useCurrentUser();
    const dateHelper = useDateHelper();
    const { loadUrl } = useNavigationHelper();
    const brandConfig = useTargetBrandConfig();
    const { allPastDueCourses, allPastDueProjects } = usePastDueCoursesAndProjects();

    // Get user's program inclusion data and curriculum status with stable references
    const programInclusion = useMemo(() => getProgramInclusion(currentUser), [currentUser]);
    const status = useMemo(() => getCurriculumStatus(currentUser), [currentUser]);
    const cohort = useMemo(() => getCohort(currentUser), [currentUser]);
    const programTypeConfig = useMemo(() => (cohort ? ProgramTypeConfigs[cohort.programType] : null), [cohort]);

    // Check for academic probation status
    // Academic probation takes precedence over curriculum status in display
    const hasAcademicProbationStatus = useMemo(
        () => !!programInclusion?.academicProbationStatus,
        [programInclusion?.academicProbationStatus],
    );

    const completionDeadline = useMemo(() => {
        if (!cohort) return null;
        return new Date(cohort.completionDeadline * 1000);
    }, [cohort]);

    const pastGraduationDate = useMemo(
        () => cohort?.graduationDate && new Date(cohort.graduationDate * 1000) < new Date(),
        [cohort],
    );

    const isUnfinishedCertPastGraduationDate = useMemo(() => {
        if (!cohort) return false;
        return (
            !!completionDeadline &&
            isPreGraduationStatus(status) &&
            refersToGraduation(cohort.programType) &&
            supportsCertificate(cohort.programType) &&
            pastGraduationDate
        );
    }, [cohort, pastGraduationDate, completionDeadline, status]);

    /** Show the completion deadline for ExecEd users who are pre-grad state after graduation date */
    const dateToDisplay = useMemo(() => {
        if (!cohort) return null;
        return isUnfinishedCertPastGraduationDate
            ? formattedUserFacingMonthDayYearLong(completionDeadline)
            : formattedUserFacingMonthDayYearLong(new Date(cohort.graduationDate * 1000), false);
    }, [cohort, isUnfinishedCertPastGraduationDate, completionDeadline]);

    const preGraduationText = useMemo<I18NLocale | null>(() => {
        if (!cohort) return null;
        if (isUnfinishedCertPastGraduationDate) {
            return { key: 'certificateExtensionNotice' };
        }
        if (showsDiplomaCheckInWhenInFinishedState(cohort.programType)) {
            return { key: 'completeDiplomaCheck' };
        }
        if (showsCertificateCheckInWhenInFinishedState(cohort.programType)) {
            return { key: 'youWillBeNotifiedWhenCertIsAvailable' };
        }
        return null;
    }, [cohort, isUnfinishedCertPastGraduationDate]);

    const preGraduationMobileTitleAndPrefix = useMemo<{ title?: I18NLocale; prefix?: I18NLocale } | null>(() => {
        if (!cohort?.programType) return null;
        if (showsDiplomaCheckInWhenInFinishedState(cohort.programType)) {
            return { title: { key: 'mobilePrepareForGraduation' } };
        }
        if (showsCertificateCheckInWhenInFinishedState(cohort.programType)) {
            return { title: { key: 'mobileCheckYourCertificate' } };
        }
        // afaik, this is not a real state. Anyone who can be finished should be caught
        // by one of the above cases. But, adding a backstop just in case.
        return { title: { key: 'finished' }, prefix: { key: 'mobileTitleStatusPrefix' } };
    }, [cohort?.programType]);

    const preGraduationSubtitle = useMemo<I18NLocale | null>(() => {
        if (!cohort) return null;
        if (refersToGraduation(cohort.programType)) {
            return { key: 'graduationDateSubtitle' };
        }
        if (supportsCertificate(cohort.programType) && !isUnfinishedCertPastGraduationDate) {
            return { key: 'certificateCompletionDateSubtitle' };
        }
        if (isUnfinishedCertPastGraduationDate) {
            return { key: 'certificateCompletionDeadlineSubtitle' };
        }
        if (!refersToGraduation(cohort.programType) && !supportsCertificate(cohort.programType)) {
            return { key: 'programCompletionDateSubtitle' };
        }
        return null;
    }, [cohort, isUnfinishedCertPastGraduationDate]);

    // Configuration object mapping each curriculum status to display settings
    const statusConfig = useMemo<Record<CurriculumStatus | 'academicProbation', StatusConfigPromise> | null>(
        // Making these async for future flexibility
        // oxlint-disable-next-line max-lines-per-function
        () => {
            if (!cohort?.startDate || !programTypeConfig) return null;
            return {
                [CurriculumStatus.Week0]: async () => ({
                    icon: faCalendar,
                    message: { key: !cohort?.slackRooms?.length ? 'week0MessageNoSlack' : 'week0Message' },
                    showCoursesAndProjects: false,
                    headerSubtitle: { key: 'startDate' },
                    headerTitle: {
                        key: 'week0Title',
                        params: {
                            startDate: dateHelper.formattedUserFacingMonthDayYearLong(
                                new Date(cohort.startDate * 1000),
                                false,
                            ),
                        },
                    },
                    cta: {
                        buttonId: 'viewSchedule',
                        text: { key: cohort?.externalScheduleUrl ? 'viewFullSchedule' : 'scheduleComingSoon' },
                        onPress: () => {
                            if (!cohort?.externalScheduleUrl) return; // noop
                            loadUrl(cohort.externalScheduleUrl, '_blank');
                        },
                        disabled: !cohort?.externalScheduleUrl,
                    },
                    mobileTitle: {
                        key: 'week0Title',
                        params: {
                            startDate: dateHelper.formattedUserFacingMonthDayYearLong(
                                new Date(cohort.startDate * 1000),
                                false,
                            ),
                        },
                    },
                    mobileTitlePrefix: { key: 'mobileTitleStartDatePrefix' },
                }),
                [CurriculumStatus.Week1]: async () => ({
                    icon: 'default',
                    message: { key: 'week1Message' },
                    showCoursesAndProjects: false,
                    headerSubtitle: { key: programTypeConfig?.isExecEd ? 'welcomeToThe' : 'welcomeTo' },
                    headerTitle: {
                        key: 'programTitle',
                        params: {
                            programTitle: programTypeConfig.isExecEd
                                ? cohort.title
                                : programTypeConfig.programBoxTitle?.(cohort),
                        },
                    },
                    // The `programTitle` property for Exec Ed program types can be really long and cause the Failed and Week1
                    // headings to wrap to 3+ lines, which causes the text to overflow its container. We decided the best course of action
                    // was to show only the `fullTitle` property for Exec Ed programs, which is shorter and doesn't cause the text to overflow,
                    // even at our lowest supported viewport width (360px).
                    mobileTitle: {
                        key: 'programTitle',
                        params: {
                            programTitle: programTypeConfig.isExecEd
                                ? programTypeConfig.fullTitle(cohort)
                                : programTypeConfig.programBoxTitle?.(cohort),
                        },
                    },
                    mobileTitlePrefix: programTypeConfig.isExecEd ? undefined : { key: 'mobileTitleWelcomePrefix' },
                }),
                [CurriculumStatus.OnTrack]: async () => ({
                    icon: faEllipsis,
                    message: { key: 'checkScheduleMessage' },
                    showCoursesAndProjects: false,
                    headerSubtitle: { key: 'status' },
                    headerTitle: { key: 'onTrack' },
                    mobileTitle: { key: 'onTrack' },
                    mobileTitlePrefix: { key: 'mobileTitleStatusPrefix' },
                }),
                [CurriculumStatus.OnTrackFinished]: async () => ({
                    icon: faCheck,
                    message: { key: 'goodWorkMessage' },
                    showCoursesAndProjects: false,
                    headerSubtitle: { key: 'status' },
                    headerTitle: { key: 'onTrack' },
                    mobileTitle: { key: 'onTrack' },
                    mobileTitlePrefix: { key: 'mobileTitleStatusPrefix' },
                }),
                [CurriculumStatus.NotOnTrack]: async () => ({
                    icon: faAlarmClock,
                    message: null,
                    showCoursesAndProjects: true,
                    headerSubtitle: { key: 'status' },
                    headerTitle: { key: 'notOnTrack' },
                    mobileTitle: { key: 'notOnTrack' },
                    mobileTitlePrefix: { key: 'mobileTitleStatusPrefix' },
                }),
                [CurriculumStatus.AlmostThere]: async () => ({
                    icon: faAlarmClock,
                    message: null,
                    showCoursesAndProjects: true,
                    headerSubtitle: { key: 'status' },
                    headerTitle: { key: 'almostThere' },
                    mobileTitle: { key: 'almostThere' },
                    mobileTitlePrefix: { key: 'mobileTitleStatusPrefix' },
                }),
                [CurriculumStatus.Failed]: async () => ({
                    icon: 'default',
                    message: {
                        key: 'failedMessage',
                        params: {
                            supportEmail: (
                                <InlineLink
                                    href={`mailto:${programTypeConfig?.supportEmailUsername}@${programTypeConfig?.supportEmailDomain}`}
                                    target="_blank"
                                >
                                    {programTypeConfig?.supportEmailUsername}@{programTypeConfig?.supportEmailDomain}
                                </InlineLink>
                            ),
                        },
                    },
                    showCoursesAndProjects: false,
                    headerSubtitle: {
                        key: 'brandNameShort',
                        params: { brandNameShort: brandConfig.brandNameShort },
                    },
                    headerTitle: {
                        key: 'programTitleShort',
                        params: {
                            programTitleShort:
                                programTypeConfig?.standaloneShortProgramTitle ?? programTypeConfig?.shortProgramTitle,
                        },
                    },
                    mobileTitle: {
                        key: 'programTitle',
                        params: {
                            programTitle: programTypeConfig.isExecEd
                                ? programTypeConfig.fullTitle(cohort)
                                : programTypeConfig.programBoxTitle?.(cohort),
                        },
                    },
                }),
                // Note: Some curriculum statuses, like Graduated and Honors, are included for completeness,
                // but the student dashboard isn't actually configured to display the schedule_status box for these dashboard states.
                [CurriculumStatus.Graduated]: async () => ({
                    icon: faGraduationCap,
                    message: { key: 'graduatedMessage' },
                    showCoursesAndProjects: false,
                    headerSubtitle: null,
                    headerTitle: { key: 'graduated' },
                }),
                [CurriculumStatus.Honors]: async () => ({
                    icon: faGraduationCap,
                    message: { key: 'graduatedMessage' },
                    showCoursesAndProjects: false,
                    headerSubtitle: null,
                    headerTitle: { key: 'graduated' },
                }),
                [CurriculumStatus.PreGraduationFinished]: async () => ({
                    icon: 'default',
                    message: preGraduationText,
                    showCoursesAndProjects: false,
                    headerSubtitle: preGraduationSubtitle,
                    headerTitle: {
                        key: 'dateToDisplay',
                        params: { dateToDisplay },
                    },
                    mobileTitle: preGraduationMobileTitleAndPrefix?.title,
                    mobileTitlePrefix: preGraduationMobileTitleAndPrefix?.prefix,
                }),
                [CurriculumStatus.PreGraduationNotOnTrack]: async () => ({
                    icon: faCalendar,
                    message: preGraduationText,
                    showCoursesAndProjects: true,
                    headerSubtitle: preGraduationSubtitle,
                    headerTitle: { key: 'dateToDisplay', params: { dateToDisplay } },
                    mobileTitle: preGraduationMobileTitleAndPrefix?.title,
                    mobileTitlePrefix: preGraduationMobileTitleAndPrefix?.prefix,
                }),
                [CurriculumStatus.PreGraduationAlmostThere]: async () => ({
                    icon: faCalendar,
                    message: preGraduationText,
                    showCoursesAndProjects: true,
                    headerSubtitle: preGraduationSubtitle,
                    headerTitle: { key: 'dateToDisplay', params: { dateToDisplay } },
                    mobileTitle: preGraduationMobileTitleAndPrefix?.title,
                    mobileTitlePrefix: preGraduationMobileTitleAndPrefix?.prefix,
                }),
                [CurriculumStatus.MissingGraduationRequirements]: async () => ({
                    icon: faAlarmClock,
                    message: null,
                    showCoursesAndProjects: true,
                    headerSubtitle: { key: 'status' },
                    headerTitle: { key: 'missingGraduationRequirements' },
                    mobileTitle: { key: 'missingGraduationRequirements' },
                    mobileTitlePrefix: { key: 'mobileTitleStatusPrefix' },
                }),
                // Here for completeness, but the box is hidden if the user is expelled/withdrawn.
                // See 'shouldHide' in sidebar box configs.
                [CurriculumStatus.WithdrawnOrExpelled]: async () => ({
                    icon: faExclamationTriangle,
                    message: null,
                    showCoursesAndProjects: false,
                    headerSubtitle: { key: 'status' },
                    headerTitle: { key: 'withdrawnOrExpelled' },
                    mobileTitle: { key: 'withdrawnOrExpelled' },
                    mobileTitlePrefix: { key: 'mobileTitleStatusPrefix' },
                }),
                // Academic probation config - included in main config to prevent recreation
                academicProbation: async () => ({
                    icon: faAlarmClock,
                    message: {
                        key: 'academicProbationContactMessage',
                        params: {
                            academicAdvisingEmail: (
                                <InlineLink
                                    href={`mailto:academicadvising@${programTypeConfig?.supportEmailDomain}`}
                                    target="_blank"
                                >
                                    {`academicadvising@${programTypeConfig?.supportEmailDomain}`}
                                </InlineLink>
                            ),
                            advisingScheduleLink: (
                                <InlineLink href={ACADEMIC_PROBATION_ADVISING_LINK} target="_blank">
                                    {t('here')}
                                </InlineLink>
                            ),
                        },
                    },
                    showCoursesAndProjects: true,
                    headerSubtitle: { key: 'status' },
                    headerTitle: { key: 'academicProbation' },
                    mobileTitle: { key: 'academicProbation' },
                    mobileTitlePrefix: { key: 'mobileTitleStatusPrefix' },
                }),
            };
        },
        [
            cohort,
            dateHelper,
            loadUrl,
            programTypeConfig,
            brandConfig.brandNameShort,
            t,
            dateToDisplay,
            preGraduationSubtitle,
            preGraduationText,
            preGraduationMobileTitleAndPrefix,
        ],
    );

    // Get the appropriate config based on status and academic probation
    const config = useMemo(() => {
        if (!statusConfig) return null;
        if (hasAcademicProbationStatus) {
            return statusConfig.academicProbation;
        }
        return status ? statusConfig[status] : null;
    }, [status, statusConfig, hasAcademicProbationStatus]);

    useEffect(() => {
        config?.().then(c => {
            setResolvedConfig(c);
        });
    }, [config]);

    // Return stable object reference to prevent unnecessary re-renders
    return useMemo(
        () => ({
            statusConfig: resolvedConfig,
            allPastDueCourses,
            allPastDueProjects,
        }),
        [resolvedConfig, allPastDueCourses, allPastDueProjects],
    );
};
